import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Store, User, Loader2 } from 'lucide-react';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const absUrl = (u) => (!u ? null : (u.startsWith('http') || u.startsWith('data:') ? u : `${API_URL}${u}`));

export const GlobalSearch = ({ className = '' }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ shops: [], players: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults({ shops: [], players: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/public/search', { params: { q: q.trim() } });
        setResults(res.data);
        setOpen(true);
      } catch (e) {
        setResults({ shops: [], players: [] });
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(''); navigate(path); };
  const hasResults = results.shops.length > 0 || results.players.length > 0;

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666] pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (hasResults) setOpen(true); }}
          placeholder="Rechercher une boutique ou un joueur"
          className="w-full bg-[#1a1a1a] border border-[#333] rounded-full pl-10 pr-9 py-2 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#FF6B35] transition-colors"
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666] animate-spin" />}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute z-[60] mt-2 w-full min-w-[280px] rounded-xl bg-[#1a1a1a] border border-[#333] shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {!hasResults && !loading && (
            <div className="px-4 py-6 text-center text-[#666] text-sm">Aucun résultat pour « {q} »</div>
          )}

          {results.shops.length > 0 && (
            <div className="py-1">
              <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#666] font-semibold">Boutiques</p>
              {results.shops.map((s) => (
                <button key={s.id} onClick={() => go(`/${s.slug}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#252525] transition-colors text-left" data-testid={`search-shop-${s.slug}`}>
                  {absUrl(s.logo_url) ? (
                    <img src={absUrl(s.logo_url)} alt={s.name} className="w-8 h-8 rounded-full object-cover border border-[#333]" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#252525] flex items-center justify-center"><Store className="w-4 h-4 text-[#FF6B35]" /></div>
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.name}</p>
                    <p className="text-[#666] text-xs truncate">{s.address || 'Boutique'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.players.length > 0 && (
            <div className="py-1 border-t border-[#333]">
              <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#666] font-semibold">Joueurs</p>
              {results.players.map((p) => (
                <button key={p.id} onClick={() => go(`/user/${p.id}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#252525] transition-colors text-left" data-testid={`search-player-${p.id}`}>
                  {absUrl(p.avatar_url) ? (
                    <img src={absUrl(p.avatar_url)} alt={p.pseudo} className="w-8 h-8 rounded-full object-cover border border-[#333]" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#252525] flex items-center justify-center"><User className="w-4 h-4 text-[#4ECDC4]" /></div>
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{p.pseudo || p.name}</p>
                    <p className="text-[#666] text-xs truncate">{p.role === 'ORG' ? (p.company_name || 'Boutique') : 'Joueur'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
